<script setup lang="ts">
import type { Column } from '@tanstack/vue-table'
import type { HTMLAttributes } from 'vue'
import { computed } from 'vue'
import { cn } from '#ui-kit/lib/utils'

type EdgeAwareColumn = Pick<Column<unknown, unknown>, 'getIsFirstColumn' | 'getIsLastColumn'>

const props = withDefaults(
  defineProps<{
    column: EdgeAwareColumn
    /**
     * `center` (default) lets the cell centre the control in the row.
     * `top` fills the cell height and pins the control 8px from the row's top
     * edge, for rows whose height is driven by a growing control (a textarea):
     * the fixed-height controls beside it stay on the top line instead of
     * drifting to the middle of the taller row.
     */
    align?: 'center' | 'top'
    class?: HTMLAttributes['class']
  }>(),
  {
    align: 'center',
  },
)

// 4px per cell plus a 4px row inset on the outer columns: every control sits
// 8px from its neighbours and from the table edges.
const padding = computed(() => [
  props.align === 'top' ? 'h-full py-2' : 'py-1',
  props.column.getIsFirstColumn() ? 'pl-2' : 'pl-1',
  props.column.getIsLastColumn() ? 'pr-2' : 'pr-1',
])
</script>

<template>
  <div :class="cn(padding, props.class)">
    <slot />
  </div>
</template>
